import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import "../styles/LoginPage.css";

export default function OAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOAuthCallback } = useAuth();
  const [status, setStatus] = useState("Signing in...");
  const effectRan = useRef(false);

  useEffect(() => {
    if (effectRan.current) return;
    effectRan.current = true;
    let mounted = true;

    (async () => {
      // Always clear the stored redirect immediately — before any early returns —
      // so a failed or missing-code callback doesn't leave a stale path in
      // localStorage that would silently redirect the next successful login.
      const storedRedirect = localStorage.getItem('sf_auth_redirect');
      localStorage.removeItem('sf_auth_redirect');

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const redirectUri = window.location.origin + window.location.pathname;
      if (!code) {
        if (mounted) setStatus("Missing code in callback");
        return;
      }

      const result = await handleOAuthCallback({ code, state, redirectUri });
      if (result.error) {
        // Only update UI state if still mounted; navigate is skipped on error.
        if (mounted) setStatus(result.error);
        return;
      }
      // navigate() is a router action, not a React state update — it must fire
      // even when React StrictMode's simulated unmount has set mounted=false.
      // Without this, the user stays stuck on "Signing in..." even though the
      // session cookie was successfully set by the POST /auth/token response.
      // Recover the intended destination:
      // 1. storedRedirect (set by LoginPage before the hard OAuth browser redirect)
      // 2. location.state (only populated by in-app navigate calls — email flow)
      // 3. Fallback to "/"
      // Validate: must be an internal path (starts with /) to prevent open-redirect.
      const candidate = storedRedirect || location.state?.redirectTo || "/chat";
      const redirectTo = candidate.startsWith('/') && !candidate.startsWith('//') ? candidate : "/chat";
      navigate(result.isNew ? "/register" : redirectTo, { replace: true, state: { redirectTo } });
    })();

    return () => { mounted = false; };
    // location.state is intentionally omitted from deps: it is read once inside
    // the async callback after the effectRan guard, and including it would cause
    // a re-run after navigate() updates location.state on success.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOAuthCallback, navigate]);

  return (
    <div className="login-wrapper">
      <div className="login-card" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
        {status === "Signing in..." ? (
          <>
            <div className="spinner"></div>
            <div style={{ marginTop: '1rem', color: '#b5b9c9' }}>{status}</div>
          </>
        ) : (
          <div style={{ color: '#ffb8c4' }}>{status}</div>
        )}
      </div>
    </div>
  );
}
